/**
 * Uebersicht /evaluations (D-10/D-21): fuenf Karten in der vom Server gelieferten Reihenfolge,
 * je Karte der aktuelle Zeitraum mit den Punkten der Statusleiste (Meldestand, gesperrt,
 * zurueckrechenbar, Handentscheidungen). Kein Abschluss-Fortschrittsbalken mehr -- ein einziger
 * Aufruf liefert alles, keine eigene Zaehlung im Frontend.
 */
import type { KeyboardEvent } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Alert,
  Badge,
  Card,
  Group,
  Skeleton,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { evaluationTableApi } from '../../services/evaluationTableApi';
import { getApiErrorMessage } from '../../utils/errorHandling';
import { PageHeader } from '../../components/ui/PageHeader';
import type { EvaluationOverviewEntry } from '../../types/evaluationTable';

function wordForm(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural;
}

function cardPoints(entry: EvaluationOverviewEntry): string[] {
  if (!entry.hasData || !entry.reported) {
    return [];
  }
  const points = [
    `${entry.reported.reported} von ${entry.reported.expected} genehmigt`,
    `${entry.lockedCount} gesperrt`,
    `${entry.recoverableCount} zurückrechenbar`,
  ];
  if (entry.decisionCount > 0) {
    points.push(
      `${entry.decisionCount} ${wordForm(entry.decisionCount, 'Handentscheidung', 'Handentscheidungen')}`
    );
  }
  return points;
}

function cardHref(entry: EvaluationOverviewEntry): string {
  if (entry.hasData && entry.year !== null && entry.period !== null) {
    return `/evaluations/${entry.evaluationType}?year=${entry.year}&period=${entry.period}`;
  }
  return `/evaluations/${entry.evaluationType}`;
}

export function EvaluationsHubPage() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<EvaluationOverviewEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    evaluationTableApi
      .getOverview()
      .then((response) => {
        if (!cancelled) {
          setEntries(response.types);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(
            getApiErrorMessage(
              err,
              'Die Übersicht konnte nicht geladen werden.'
            )
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  function handleActivate(entry: EvaluationOverviewEntry) {
    navigate(cardHref(entry));
  }

  function handleKeyDown(
    event: KeyboardEvent<HTMLDivElement>,
    entry: EvaluationOverviewEntry
  ) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleActivate(entry);
    }
  }

  return (
    <Stack gap="lg">
      <PageHeader
        title="Auswertungen"
        breadcrumb={{ label: 'Treuhänder' }}
        description="Wählen Sie eine Auswertung, um die Daten einzusehen."
      />

      {error && (
        <Alert color="red" title="Fehler">
          {error}
        </Alert>
      )}

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
        {loading
          ? Array.from({ length: 5 }).map((_, index) => (
              <Card key={index} shadow="xs" padding="lg" radius="md" withBorder>
                <Stack gap="sm">
                  <Skeleton height={20} width="60%" />
                  <Skeleton height={14} width="80%" />
                  <Skeleton height={14} width="50%" />
                </Stack>
              </Card>
            ))
          : entries.map((entry) => (
              <Card
                key={entry.evaluationType}
                role="link"
                tabIndex={0}
                shadow="xs"
                padding="lg"
                radius="md"
                withBorder
                onClick={() => handleActivate(entry)}
                onKeyDown={(event) => handleKeyDown(event, entry)}
                style={{
                  cursor: 'pointer',
                  transition: 'box-shadow 150ms ease, transform 150ms ease',
                }}
                styles={{
                  root: {
                    '&:hover': {
                      boxShadow: 'var(--mantine-shadow-md)',
                      transform: 'translateY(-2px)',
                    },
                  },
                }}
              >
                <Stack gap="sm">
                  <Group justify="space-between" align="flex-start">
                    <Title order={4}>{entry.label}</Title>
                    <Badge variant="light" color="vdzGray" size="sm">
                      {entry.periodicity}
                    </Badge>
                  </Group>
                  {entry.hasData ? (
                    <>
                      <Text fz="sm" fw={500}>
                        {entry.periodLabel}
                      </Text>
                      <Text fz="sm" c="vdzGray.6">
                        {cardPoints(entry).join(' · ')}
                      </Text>
                    </>
                  ) : (
                    <Text fz="sm" c="vdzGray.6">
                      Noch keine Daten
                    </Text>
                  )}
                </Stack>
              </Card>
            ))}
      </SimpleGrid>
    </Stack>
  );
}
